import { format, parse } from 'date-fns';

export const CERTIFICATION_META = {
  delivers_with_impact: {
    displayName: 'High-Performing Engineering',
    verificationTagline: 'Consistent delivery excellence, verified by',
    shortDescription:
      'Recognizes sustained delivery momentum, consistent execution, and measurable product contribution.',
    criteria: [
      'Delivery consistency above company benchmark',
      'Strong monthly productivity score',
      'Visible contribution momentum'
    ],
    summary:
      'A GitMe-verified signal of sustained delivery momentum and measurable engineering contribution.',
    overview:
      'This GitMe achievement is based on observed engineering delivery patterns across the evaluated period.',
    detail:
      'It reflects an individual or engineering organization that consistently converts work into visible product movement without relying on short-lived spikes.',
    tone: 'Signals strong engineering execution in a credible, professional, and non-inflated way.',
    accent: {
      base: '#C68A2B',
      soft: '#F6E6BF',
      border: '#E7C881',
      surface: 'from-amber-50 via-white to-orange-50'
    }
  },
  delivers_with_precision: {
    displayName: 'AI-Efficient Engineering Leader',
    verificationTagline:
      'AI-powered execution with engineering rigor, verified by',
    shortDescription:
      'Recognizes meaningful AI leverage and saved baseline effort while maintaining engineering discipline and delivery quality.',
    criteria: [
      'Meaningful effort saved through AI leverage',
      'Sustainable development rhythm',
      'No quality regression detected'
    ],
    summary:
      'A GitMe-verified signal of meaningful AI leverage with stable engineering discipline.',
    overview:
      'This GitMe achievement is based on estimated baseline effort saved through AI and automation, measured alongside delivery consistency and quality stability.',
    detail:
      'It reflects a team that appears able to reduce the human effort required for the same output through AI leverage while preserving dependable engineering practices and long-term delivery confidence. It does not measure code authorship or AI-generated lines.',
    tone: 'Signals modern AI fluency without implying speed at the expense of quality.',
    accent: {
      base: '#5B5BD6',
      soft: '#DDDDFE',
      border: '#B6B8FF',
      surface: 'from-indigo-50 via-white to-sky-50'
    }
  },
  built_to_last: {
    displayName: 'Code Quality Leader',
    verificationTagline: 'Engineered for the long run, verified by',
    shortDescription:
      'Recognizes disciplined code review behavior, maintainability, and quality-focused engineering standards.',
    criteria: [
      'Strong review discipline',
      'Low rework / defect signal',
      'Maintainable contribution patterns'
    ],
    summary:
      'A GitMe-verified signal of disciplined review culture and maintainable engineering standards.',
    overview:
      'This GitMe achievement is based on delivery quality, review discipline, and maintainability-oriented contribution patterns.',
    detail:
      'It reflects engineering work that appears controlled, review-aware, and optimized for healthy long-term code ownership rather than reactive output.',
    tone: 'Signals disciplined engineering quality and sustainable review behavior.',
    accent: {
      base: '#138A62',
      soft: '#D6F2E6',
      border: '#8BDBB9',
      surface: 'from-emerald-50 via-white to-teal-50'
    }
  }
};

export const CERTIFICATION_DISPLAY_ORDER = [
  'delivers_with_impact',
  'built_to_last',
  'delivers_with_precision'
];

const CERTIFICATION_NAME_TO_KEY = {
  'high-performing engineering': 'delivers_with_impact',
  'high performance engineer': 'delivers_with_impact',
  'code quality leader': 'built_to_last',
  'ai-efficient engineering leader': 'delivers_with_precision'
};

export const getCertificationThemeKey = badge => {
  if (badge?.badge_key && CERTIFICATION_META[badge.badge_key]) {
    return badge.badge_key;
  }

  const normalizedDisplayName = String(badge?.display_name || '')
    .trim()
    .toLowerCase();

  if (CERTIFICATION_NAME_TO_KEY[normalizedDisplayName]) {
    return CERTIFICATION_NAME_TO_KEY[normalizedDisplayName];
  }

  const normalizedShareCopy = String(badge?.share_copy || '')
    .trim()
    .toLowerCase();

  if (normalizedShareCopy.includes('code quality leader')) {
    return 'built_to_last';
  }

  if (normalizedShareCopy.includes('ai-efficient engineering leader')) {
    return 'delivers_with_precision';
  }

  if (
    normalizedShareCopy.includes('high-performing engineering') ||
    normalizedShareCopy.includes('high performance engineer')
  ) {
    return 'delivers_with_impact';
  }

  return 'delivers_with_impact';
};

export const getCertificationMeta = badge =>
  CERTIFICATION_META[getCertificationThemeKey(badge)] ||
  CERTIFICATION_META.delivers_with_impact;

export const getCertificationDisplayName = badge =>
  (badge?.rule_version === 'v2' && badge?.display_name) ||
  getCertificationMeta(badge)?.displayName ||
  badge?.display_name ||
  'Certification';

export const getCertificationVerificationTagline = badge =>
  (badge?.rule_version === 'v2' && 'Engineering signals verified by') ||
  getCertificationMeta(badge)?.verificationTagline ||
  'Engineering excellence verified by';

const formatCertificationMonth = yearMonth => {
  if (!yearMonth) return '';

  try {
    return format(parse(yearMonth, 'yyyy-MM', new Date()), 'MMMM yyyy');
  } catch {
    return yearMonth;
  }
};

export const getCertificationValidityCopy = badge => {
  const validThrough = formatCertificationMonth(
    badge?.valid_through_end_year_month
  );

  return validThrough ? `Verified through ${validThrough}.` : '';
};

export const getCertificationSignalCopy = badge => {
  if (badge?.rule_version === 'v2') {
    const criteria = badge?.qualification_snapshot?.matched_strengths || [];
    const isAI = badge?.badge_key === 'delivers_with_precision';
    const aiRequired =
      badge?.qualification_snapshot?.mandatory_strengths?.includes(
        'effective_ai_acceleration'
      ) || badge?.rule_revision === 'ai-required-2026-10';
    return {
      heading: 'Matched criteria',
      buttonLabel: 'Matched criteria',
      overview:
        'This achievement is based on deterministic delivery, durability, maintenance, and AI-leverage signals from a completed 12-month period.',
      detail: isAI
        ? aiRequired
          ? 'Effective AI acceleration is required, together with at least three qualifying signals and no blocking signal. It does not use review, defect, coverage, rollback, or rework data.'
          : 'Issued under the earlier engineering-signal rules. Its original qualification and validity remain in effect; newer AI certifications require effective AI acceleration.'
        : 'It does not use review, defect, coverage, rollback, or rework data.',
      summary: 'Verified against measured engineering signals.',
      bullets: criteria.map(item => String(item).replace(/_/g, ' '))
    };
  }
  const meta = getCertificationMeta(badge);

  return {
    heading: 'What this achievement reflects',
    buttonLabel: 'What this achievement reflects',
    overview: meta.overview,
    detail: meta.detail,
    summary: meta.summary,
    bullets: meta.criteria
  };
};

export const getCertificationLinkedInCopy = badge => {
  const meta = getCertificationMeta(badge);
  const companyName = badge?.company_name || 'Our engineering team';
  const validThrough = badge?.valid_through_end_year_month
    ? ` Valid through ${badge.valid_through_end_year_month}.`
    : '';
  const shareUrl = badge?.share_url
    ? `\n\nVerification: ${badge.share_url}`
    : '';

  const displayName = getCertificationDisplayName(badge);
  const description =
    badge?.rule_version === 'v2'
      ? 'Verified by GitMe from deterministic engineering signals across a completed 12-month period.'
      : `${meta.shortDescription}\n\nVerified by GitMe based on engineering delivery, code quality, and estimated AI leverage.`;

  return `${companyName} earned the GitMe ${displayName} achievement.${validThrough}

${description}${shareUrl}`;
};
